import React, { useCallback, useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { ACTION_SUBJECT_ID, INPUT_METHOD } from '@atlaskit/editor-common/analytics/types/enums';
import { getDomRefFromSelection } from '@atlaskit/editor-common/get-dom-ref-from-selection';
import Popup from '@atlaskit/editor-common/Popup';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { UiComponentFactoryParams } from '@atlaskit/editor-common/types/ui-components';
import { OutsideClickTargetRefContext } from '@atlaskit/editor-common/ui-react';
import withOuterListeners from '@atlaskit/editor-common/ui-react/with-react-editor-view-outer-listeners';
import { useSharedPluginState } from '@atlaskit/editor-common/useSharedPluginState';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/useSharedPluginStateSelector';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { akEditorFloatingDialogZIndex } from '@atlaskit/editor-shared-styles/constants';
import { EmojiPicker } from '@atlaskit/emoji';
import type { EmojiId } from '@atlaskit/emoji';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { EmojiPlugin } from '../emojiPluginType';
import { setInlineEmojiPopupOpen } from '../pm-plugins/actions';

const PopupWithListeners = withOuterListeners(Popup);

const emojiPopupMessages = {
	emojiPickerAriaLabel: {
		id: 'fabric.emoji.picker.aria.label',
		defaultMessage: 'Emoji picker',
		description: 'Accessible label for the emoji picker popup',
	},
};

type InlineEmojiPopupProps = Pick<
	UiComponentFactoryParams,
	'popupsBoundariesElement' | 'popupsMountPoint' | 'popupsScrollableElement'
> & {
	api: ExtractInjectionAPI<EmojiPlugin>;
	contentId?: string;
	editorView: EditorView;
};

export const InlineEmojiPopupOld = ({
	api,
	popupsMountPoint,
	popupsBoundariesElement,
	popupsScrollableElement,
	editorView,
	contentId,
	onClose,
}: InlineEmojiPopupProps & { onClose: () => void }): React.JSX.Element | null => {
	const { emojiProvider, inlineEmojiPopupOpen: isOpen } =
		useSharedPluginState(api, ['emoji'])?.emojiState ?? {};

	const intl = useIntl();
	useEffect(() => {
		if (isOpen && fg('platform_editor_ease_of_use_metrics')) {
			api?.core.actions.execute(
				api?.metrics?.commands.handleIntentToStartEdit({
					shouldStartTimer: false,
					shouldPersistActiveSession: true,
				}),
			);
		}
	}, [isOpen, api]);

	const handleOnClose = useCallback(() => {
		if (fg('platform_editor_ease_of_use_metrics')) {
			api?.core.actions.execute(api?.metrics?.commands.startActiveSessionTimer());
		}
		onClose?.();
	}, [onClose, api]);

	const focusEditor = useCallback(() => {
		// use requestAnimationFrame to run this async after the call
		requestAnimationFrame(() => editorView.focus());
	}, [editorView]);

	const handleSelection = useCallback(
		(emojiId: EmojiId) => {
			api.core.actions.execute(api.emoji.commands.insertEmoji(emojiId, INPUT_METHOD.PICKER));
			handleOnClose();
		},
		[api.core.actions, api.emoji.commands, handleOnClose],
	);

	if (!isOpen || !emojiProvider) {
		return null;
	}

	const domRef = getDomRefFromSelection(
		editorView,
		ACTION_SUBJECT_ID.PICKER_EMOJI,
		api?.analytics?.actions,
	);
	return (
		<PopupWithListeners
			ariaLabel={intl.formatMessage(emojiPopupMessages.emojiPickerAriaLabel)}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			offset={[0, 12]}
			mountTo={popupsMountPoint}
			boundariesElement={popupsBoundariesElement}
			scrollableElement={popupsScrollableElement}
			zIndex={akEditorFloatingDialogZIndex}
			fitHeight={350}
			fitWidth={350}
			target={domRef}
			onUnmount={focusEditor}
			focusTrap
			preventOverflow
			handleClickOutside={handleOnClose}
			handleEscapeKeydown={handleOnClose}
			captureClick
		>
			<OutsideClickTargetRefContext.Consumer>
				{(setOutsideClickTargetRef) => (
					<EmojiPicker
						contentId={contentId}
						emojiProvider={Promise.resolve(emojiProvider)}
						onPickerRef={setOutsideClickTargetRef}
						onSelection={handleSelection}
					/>
				)}
			</OutsideClickTargetRefContext.Consumer>
		</PopupWithListeners>
	);
};

const InlineEmojiPopupContent = ({
	api,
	popupsMountPoint,
	popupsBoundariesElement,
	popupsScrollableElement,
	editorView,
	contentId,
}: InlineEmojiPopupProps) => {
	const emojiProvider = useSharedPluginStateSelector(api, 'emoji.emojiProvider');
	const intl = useIntl();

	useEffect(() => {
		if (fg('platform_editor_ease_of_use_metrics')) {
			api?.core.actions.execute(
				api?.metrics?.commands.handleIntentToStartEdit({
					shouldStartTimer: false,
					shouldPersistActiveSession: true,
				}),
			);
		}
	}, [api?.core.actions, api?.metrics?.commands]);

	const handleOnClose = useCallback(() => {
		editorView.dispatch(setInlineEmojiPopupOpen(false)(editorView.state.tr));

		if (fg('platform_editor_ease_of_use_metrics')) {
			api?.core.actions.execute(api?.metrics?.commands.startActiveSessionTimer());
		}
	}, [editorView, api.core.actions, api.metrics?.commands]);

	const focusEditor = useCallback(() => {
		// use requestAnimationFrame to run this async after the call
		requestAnimationFrame(() => editorView.focus());
	}, [editorView]);

	const handleSelection = useCallback(
		(emojiId: EmojiId) => {
			api.core.actions.execute(api.emoji.commands.insertEmoji(emojiId, INPUT_METHOD.PICKER));
			handleOnClose();
		},
		[api.core.actions, api.emoji.commands, handleOnClose],
	);

	const domRef = useMemo(
		() =>
			getDomRefFromSelection(editorView, ACTION_SUBJECT_ID.PICKER_EMOJI, api?.analytics?.actions),
		[editorView, api?.analytics?.actions],
	);

	if (!emojiProvider) {
		return null;
	}

	return (
		<PopupWithListeners
			ariaLabel={intl.formatMessage(emojiPopupMessages.emojiPickerAriaLabel)}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			offset={[0, 12]}
			mountTo={popupsMountPoint}
			boundariesElement={popupsBoundariesElement}
			scrollableElement={popupsScrollableElement}
			zIndex={akEditorFloatingDialogZIndex}
			fitHeight={350}
			fitWidth={350}
			target={domRef}
			onUnmount={focusEditor}
			focusTrap
			preventOverflow
			handleClickOutside={handleOnClose}
			handleEscapeKeydown={handleOnClose}
			captureClick
		>
			<OutsideClickTargetRefContext.Consumer>
				{(setOutsideClickTargetRef) => (
					<EmojiPicker
						contentId={contentId}
						emojiProvider={Promise.resolve(emojiProvider)}
						onPickerRef={setOutsideClickTargetRef}
						onSelection={handleSelection}
					/>
				)}
			</OutsideClickTargetRefContext.Consumer>
		</PopupWithListeners>
	);
};

export const InlineEmojiPopup: React.MemoExoticComponent<
	({
		api,
		popupsMountPoint,
		popupsBoundariesElement,
		popupsScrollableElement,
		editorView,
	}: InlineEmojiPopupProps) => React.JSX.Element | null
> = React.memo(
	({
		api,
		popupsMountPoint,
		popupsBoundariesElement,
		popupsScrollableElement,
		editorView,
		contentId,
	}: InlineEmojiPopupProps): React.JSX.Element | null => {
		const isOpen = useSharedPluginStateSelector(api, 'emoji.inlineEmojiPopupOpen');

		if (!isOpen) {
			return null;
		}

		return (
			<InlineEmojiPopupContent
				api={api}
				editorView={editorView}
				popupsBoundariesElement={popupsBoundariesElement}
				popupsMountPoint={popupsMountPoint}
				popupsScrollableElement={popupsScrollableElement}
				contentId={contentId}
			/>
		);
	},
);
